import React, { useState, useEffect, useMemo } from 'react';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  alpha,
  useTheme,
  Skeleton,
  Tooltip,
  IconButton,
  LinearProgress,
  Divider,
  Button,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupsIcon from '@mui/icons-material/Groups';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { saudeService } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { buildPainelMSContext } from '../../utils/saude-context-text';

// ─── Currency formatter ───
const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatCurrencyCompact = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

// ─── Classification color mapping (alinhado com ClassificacaoBadge) ───
const CLASSIFICACAO_COLORS: Record<string, string> = {
  'OTIMO': '#2e7d32',
  'Otimo': '#2e7d32',
  'BOM': '#1565c0',
  'Bom': '#1565c0',
  'SUFICIENTE': '#e65100',
  'Suficiente': '#e65100',
  'REGULAR': '#c62828',
  'Regular': '#c62828',
  'INSUFICIENTE': '#c62828',
  'Insuficiente': '#c62828',
};

function getClassColor(classificacao: string | undefined | null): string {
  if (!classificacao) return '#9e9e9e';
  return CLASSIFICACAO_COLORS[classificacao] || '#9e9e9e';
}

// ─── Competencias color ───
function getCompetenciasColor(count: number): string {
  if (count >= 3) return '#f44336';
  if (count === 2) return '#ff9800';
  return '#ffc107';
}

// ─── Info Tooltip helper component ───
const InfoTip: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title} arrow placement="top">
    <IconButton size="small" sx={{ ml: 0.5, p: 0.25, opacity: 0.6, '&:hover': { opacity: 1 } }}>
      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Tooltip>
);

// ─── Tab Panel helper ───
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

// ─── Coverage bar component ───
const CoverageBar: React.FC<{
  label: string;
  value: number;
  color: string;
  tooltip: string;
}> = ({ label, value, color, tooltip }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" fontWeight={500}>{label}</Typography>
        <InfoTip title={tooltip} />
      </Box>
      <Typography variant="body2" fontWeight={700} sx={{ color }}>
        {value != null ? `${value.toFixed(1)}%` : '-'}
      </Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={Math.min(value || 0, 100)}
      sx={{
        height: 10,
        borderRadius: 5,
        bgcolor: alpha(color, 0.12),
        '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: color },
      }}
    />
  </Box>
);

// ─── Benchmark row ───
const BenchmarkRow: React.FC<{
  municipio: number;
  estado: number;
  brasil: number;
  label: string;
  tooltip: string;
}> = ({ municipio, estado, brasil, label, tooltip }) => {
  const max = Math.max(municipio || 0, estado || 0, brasil || 0, 1);
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2, overflow: 'hidden' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>{label}</Typography>
          <InfoTip title={tooltip} />
        </Box>
        {[
          { name: 'Municipio', val: municipio, color: '#1565c0' },
          { name: 'Estado', val: estado, color: '#7b1fa2' },
          { name: 'Brasil', val: brasil, color: '#2e7d32' },
        ].map((row) => (
          <Box key={row.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Typography variant="caption" sx={{ minWidth: 60, color: 'text.secondary' }}>{row.name}</Typography>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={max > 0 ? ((row.val || 0) / max) * 100 : 0}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: alpha(row.color, 0.1),
                  '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: row.color },
                }}
              />
            </Box>
            <Typography variant="caption" fontWeight={700} sx={{ minWidth: 48, textAlign: 'right', color: row.color }}>
              {row.val != null ? `${row.val.toFixed(1)}%` : '-'}
            </Typography>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const SaudePainelMS: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { municipioAtivo } = useAuth();

  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Per-section loading states (progressive loading)
  const [loadingPagamento, setLoadingPagamento] = useState(true);
  const [loadingSuspensao, setLoadingSuspensao] = useState(true);
  const [loadingCoberturaAps, setLoadingCoberturaAps] = useState(true);
  const [loadingCoberturaSb, setLoadingCoberturaSb] = useState(true);
  const [loadingEquipes, setLoadingEquipes] = useState(true);
  const [loadingSimulacao, setLoadingSimulacao] = useState(true);

  // Data states
  const [resumo, setResumo] = useState<any>(null);
  const [pagamento, setPagamento] = useState<any>(null);
  const [suspensao, setSuspensao] = useState<any>(null);
  const [coberturaAps, setCoberturaAps] = useState<any>(null);
  const [coberturaSb, setCoberturaSb] = useState<any>(null);
  const [equipesHomologadas, setEquipesHomologadas] = useState<any>(null);

  // Fetch all data on mount / municipio change
  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoadingPagamento(false);
      setLoadingSuspensao(false);
      setLoadingCoberturaAps(false);
      setLoadingCoberturaSb(false);
      setLoadingEquipes(false);
      setLoadingSimulacao(false);
      return;
    }

    // Reset states
    setError(null);
    setPagamento(null);
    setSuspensao(null);
    setCoberturaAps(null);
    setCoberturaSb(null);
    setEquipesHomologadas(null);
    setResumo(null);
    setLoadingPagamento(true);
    setLoadingSuspensao(true);
    setLoadingCoberturaAps(true);
    setLoadingCoberturaSb(true);
    setLoadingEquipes(true);
    setLoadingSimulacao(true);

    // Fire all 6 requests in parallel, each updates its own state independently
    saudeService.getPagamentoMS(municipioAtivo.id)
      .then((data: any) => setPagamento(data))
      .catch(() => {})
      .finally(() => setLoadingPagamento(false));

    saudeService.getSuspensaoMS(municipioAtivo.id)
      .then((data: any) => setSuspensao(data))
      .catch(() => {})
      .finally(() => setLoadingSuspensao(false));

    saudeService.getCoberturaApsMS(municipioAtivo.id)
      .then((data: any) => setCoberturaAps(data))
      .catch(() => {})
      .finally(() => setLoadingCoberturaAps(false));

    saudeService.getCoberturaSbMS(municipioAtivo.id)
      .then((data: any) => setCoberturaSb(data))
      .catch(() => {})
      .finally(() => setLoadingCoberturaSb(false));

    saudeService.getEquipesHomologadasMS(municipioAtivo.id)
      .then((data: any) => setEquipesHomologadas(data))
      .catch(() => {})
      .finally(() => setLoadingEquipes(false));

    saudeService.getSimulacaoFinanceira(municipioAtivo.id)
      .then((sim: any) => {
        if (sim?.resumo) {
          setResumo({ simulacao: sim });
        }
      })
      .catch(() => {})
      .finally(() => setLoadingSimulacao(false));
  }, [municipioAtivo?.id]);

  // Build resumo comparison when both pagamento and simulacao are available
  const resumoComparativo = useMemo(() => {
    if (!resumo?.simulacao?.resumo || !pagamento) return resumo;
    const sim = resumo.simulacao;
    const pagBruto = pagamento?.mensagem ? 0 : (pagamento?.totais?.bruto ?? pagamento?.totais?.liquido ?? pagamento?.raw?.vlTotalBruto ?? 0);
    return {
      simulacao: {
        valorSimulado: sim.resumo.receitaMensal,
        valorMaximo: sim.resumo.receitaMaxima,
        delta: sim.resumo.receitaMensal - pagBruto,
      },
    };
  }, [resumo, pagamento]);

  // ─── Parse "02 competencias consecutivas" string → number ───
  const parseCompetencias = (val: any): number => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    const s = String(val);
    if (s.startsWith('Superior')) return 4;
    const match = s.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };

  // ─── Normalize competencia formats to "MM/YYYY" for display ───
  const normalizeComp = (comp: string): string => {
    if (!comp) return '';
    if (comp.includes('/')) return comp;
    if (comp.length === 6) return `${comp.slice(4, 6)}/${comp.slice(0, 4)}`;
    return comp;
  };

  // ─── Coverage time series merging APS + SB historico ───
  const coberturaTimeSeries = useMemo(() => {
    const apsHist = coberturaAps?.municipio?.historico;
    const sbHist = coberturaSb?.municipio?.historico;
    if ((!Array.isArray(apsHist) || apsHist.length === 0) && (!Array.isArray(sbHist) || sbHist.length === 0)) return [];

    const map = new Map<string, { aps: number; sb: number }>();

    if (Array.isArray(apsHist)) {
      for (const item of apsHist) {
        const key = normalizeComp(item.nuComp || item.competencia || '');
        if (key) map.set(key, { aps: item.qtCobertura ?? 0, sb: 0 });
      }
    }

    if (Array.isArray(sbHist)) {
      for (const item of sbHist) {
        const key = normalizeComp(item.nuCompetencia || item.competencia || '');
        if (!key) continue;
        const existing = map.get(key);
        const sbVal = item.pcCoberturaSbSf ?? item.pcCoberturaSbAps ?? 0;
        if (existing) {
          existing.sb = sbVal;
        } else {
          map.set(key, { aps: 0, sb: sbVal });
        }
      }
    }

    return Array.from(map.entries())
      .sort((a, b) => {
        const [mA, yA] = a[0].split('/');
        const [mB, yB] = b[0].split('/');
        return (parseInt(yA + mA, 10)) - (parseInt(yB + mB, 10));
      })
      .map(([comp, vals]) => ({ competencia: comp, aps: vals.aps, sb: vals.sb }));
  }, [coberturaAps, coberturaSb]);

  // ─── Build componentes table from raw MS API fields ───
  const componentes = useMemo(() => {
    const pagData = pagamento?.mensagem ? null : pagamento;
    const r = pagData?.raw;
    if (!r) return [];
    const comps: any[] = [];
    if (r.vlTotalEsf > 0 || r.qtEsfTotalPgto > 0) comps.push({ nome: 'eSF (Saude da Familia)', qtEquipes: r.qtEsfTotalPgto ?? 0, vlCusteio: r.vlFixoEsf ?? 0, vlQualidade: (r.vlVinculoEsf ?? 0) + (r.vlQualidadeEsf ?? 0), vlTotal: r.vlTotalEsf ?? 0 });
    if (r.vlTotalEap > 0 || r.qtEapTotalPgto > 0) comps.push({ nome: 'eAP (Atencao Primaria)', qtEquipes: r.qtEapTotalPgto ?? 0, vlCusteio: r.vlFixoEap ?? 0, vlQualidade: (r.vlVinculoEap ?? 0) + (r.vlQualidadeEap ?? 0), vlTotal: r.vlTotalEap ?? 0 });
    if (r.vlTotalEmulti > 0 || r.qtEmultiPagas > 0) comps.push({ nome: 'eMulti (Multiprofissional)', qtEquipes: r.qtEmultiPagas ?? 0, vlCusteio: r.vlPagamentoEmultiCusteio ?? 0, vlQualidade: r.vlPagamentoEmultiQualidade ?? 0, vlTotal: r.vlTotalEmulti ?? 0 });
    const vlTotalEsb = (r.vlPagamentoEsb40h ?? 0) + (r.vlPagamentoEsb40hQualidade ?? 0) + (r.vlPagamentoEsbChDiferenciada ?? 0) + (r.vlPagamentoEsbChDifQualidade ?? 0);
    const qtSbPagas = (r.qtSbPagamentoModalidadeI ?? 0) + (r.qtSbPagamentoModalidadeII ?? 0);
    if (vlTotalEsb > 0 || qtSbPagas > 0) comps.push({ nome: 'eSB (Saude Bucal)', qtEquipes: qtSbPagas, vlCusteio: (r.vlPagamentoEsb40h ?? 0) + (r.vlPagamentoEsbChDiferenciada ?? 0), vlQualidade: (r.vlPagamentoEsb40hQualidade ?? 0) + (r.vlPagamentoEsbChDifQualidade ?? 0), vlTotal: vlTotalEsb });
    if ((r.vlTotalAcsDireto ?? 0) + (r.vlTotalAcsIndireto ?? 0) > 0) comps.push({ nome: 'ACS (Agentes Comunitarios)', qtEquipes: r.qtAcsDiretoPgto ?? 0, vlCusteio: (r.vlTotalAcsDireto ?? 0) + (r.vlTotalAcsIndireto ?? 0), vlQualidade: 0, vlTotal: (r.vlTotalAcsDireto ?? 0) + (r.vlTotalAcsIndireto ?? 0) });
    const vlPrisional = (r.vlPagamentoPrisionalMunicipal ?? 0) + (r.vlPagamentoQualidadePrisionalMunicipal ?? 0) + (r.vlPagamentoPrisionalEstadual ?? 0) + (r.vlPagamentoQualidadePrisionalEstadual ?? 0);
    if (vlPrisional > 0) comps.push({ nome: 'eAPP (Prisional)', qtEquipes: r.qtPrisionalMunicipalPagamento ?? 0, vlCusteio: (r.vlPagamentoPrisionalMunicipal ?? 0) + (r.vlPagamentoPrisionalEstadual ?? 0), vlQualidade: (r.vlPagamentoQualidadePrisionalMunicipal ?? 0) + (r.vlPagamentoQualidadePrisionalEstadual ?? 0), vlTotal: vlPrisional });
    const vlOutros = (r.vlPagamentoIncentivoPopulacional ?? 0) + (r.vlPagamentoIaf ?? 0) + (r.vlPagamentoCeoMunicipal ?? 0) + (r.vlPagamentoCeoEstadual ?? 0) + (r.vlPagamentoLrpdMunicipal ?? 0) + (r.vlPagamentoLrpdEstadual ?? 0);
    if (vlOutros > 0) comps.push({ nome: 'Outros (Per capita, CEO, LRPD, IAF)', qtEquipes: '-', vlCusteio: vlOutros, vlQualidade: 0, vlTotal: vlOutros });
    return comps;
  }, [pagamento]);

  const dadosContexto = useMemo(() => {
    if (!pagamento && !suspensao && !coberturaAps) return undefined;
    return buildPainelMSContext({ financiamento: pagamento, suspensao, cobertura: coberturaAps });
  }, [pagamento, suspensao, coberturaAps]);

  // ─── Render guards ───
  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>Nenhum municipio selecionado</Typography>
        <Typography variant="body1" color="text.secondary">Selecione um municipio na barra lateral para visualizar o Painel MS.</Typography>
      </Box>
    );
  }

  // ─── Extract pagamento data ───
  const pagData = pagamento?.mensagem ? null : pagamento;
  const raw = pagData?.raw;
  const totalMensal = pagData?.totais?.bruto ?? pagData?.totais?.liquido ?? raw?.vlTotalBruto ?? 0;
  const classVinculo = pagData?.classificacoes?.vinculoEsfEap ?? raw?.dsClassificacaoVinculoEsfEap ?? '-';
  const classQualidadeEsf = pagData?.classificacoes?.qualidadeEsfEap ?? raw?.dsClassificacaoQualidadeEsfEap ?? '-';
  const classQualidadeMulti = pagData?.classificacoes?.qualidadeEmulti ?? raw?.dsClassificacaoQualidadeEmulti ?? '-';
  const classQualidadeSb = pagData?.classificacoes?.qualidadeSb ?? raw?.dsClassificacaoQualidadeSb ?? raw?.dsClassificacaoQualidadeEsb ?? null;

  // ─── Extract suspensao data ───
  const equipesRisco = suspensao?.equipes || [];
  const estabelecimentosRisco = suspensao?.estabelecimentos || [];
  const profissionaisRisco = suspensao?.profissionais || [];
  const totalEquipesRisco = suspensao?.resumo?.totalEquipes ?? equipesRisco.length;
  const totalEstabelecimentosRisco = suspensao?.resumo?.totalEstabelecimentos ?? estabelecimentosRisco.length;
  const totalProfissionaisRisco = suspensao?.resumo?.totalProfissionais ?? profissionaisRisco.length;
  const equipesCriticas = equipesRisco.filter((e: any) => parseCompetencias(e.qtCompetenciasConsecutivas) >= 3);

  // ─── Extract cobertura data ───
  const cobMunAtual = coberturaAps?.municipio?.atual;
  const cobEstadoObj = coberturaAps?.estado;
  const cobBrasilObj = coberturaAps?.brasil;
  const cobSbMunAtual = coberturaSb?.municipio?.atual;
  const cobSbEstadoObj = coberturaSb?.estado;
  const cobSbBrasilObj = coberturaSb?.brasil;
  const cobApsPercent = cobMunAtual?.qtCobertura ?? 0;
  const cobSbPercent = cobSbMunAtual?.pcCoberturaSbSf ?? cobSbMunAtual?.pcCoberturaSbAps ?? 0;
  const cobAcsPercent: number | null = null;
  const cobEstado = cobEstadoObj?.qtCobertura ?? 0;
  const cobBrasil = cobBrasilObj?.qtCobertura ?? 0;
  const cobSbEstado = cobSbEstadoObj?.pcCoberturaSbSf ?? cobSbEstadoObj?.pcCoberturaSbAps ?? 0;
  const cobSbBrasil = cobSbBrasilObj?.pcCoberturaSbSf ?? cobSbBrasilObj?.pcCoberturaSbAps ?? 0;
  const populacao = cobMunAtual?.qtPopulacao ?? raw?.qtPopulacao ?? 0;
  const anoReferencia = cobMunAtual?.nuAnoReferencia ?? raw?.nuAnoRefPopulacaoIbge ?? new Date().getFullYear();
  const esfCount = cobMunAtual?.qtEsf ?? 0;
  const eap30Count = cobMunAtual?.qtEap30 ?? 0;
  const eap20Count = cobMunAtual?.qtEap20 ?? 0;

  // ─── Equipes homologadas ───
  const equipesHomologadasList = Array.isArray(equipesHomologadas) ? equipesHomologadas : (equipesHomologadas?.dados || equipesHomologadas?.equipes || []);

  // ─── Skeleton helpers for per-tab loading ───
  const SkeletonCards = () => (
    <Grid container spacing={2}>
      {[1, 2, 3, 4].map((i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
        </Grid>
      ))}
    </Grid>
  );

  const SkeletonTable = () => (
    <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3, mt: 3 }} />
  );

  return (
    <>
    <Fade in>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1400, mx: 'auto' }}>
        {/* Header - always visible */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton onClick={() => navigate('/saude')} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                <AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#1565c0' }} />
                Painel Ministerio da Saude
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dados oficiais do RelatorioAPS - {municipioAtivo.nome}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tabs - always visible */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<AccountBalanceIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Financeiro Real"
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
            <Tab
              icon={<WarningAmberIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  Alertas de Suspensao
                  {!loadingSuspensao && totalEquipesRisco > 0 && (
                    <Chip
                      label={totalEquipesRisco}
                      size="small"
                      color="error"
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                    />
                  )}
                </Box>
              }
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
            <Tab
              icon={<LocalHospitalIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Cobertura APS"
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
            />
          </Tabs>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}

        {/* ═══════════════════════════════════════════════ */}
        {/* TAB 1: FINANCEIRO REAL */}
        {/* ═══════════════════════════════════════════════ */}
        <TabPanel value={tabValue} index={0}>
          <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }} icon={<AccountBalanceIcon />}>
            <Typography variant="body2">
              <strong>Repasses Federais - Dados Oficiais do MS.</strong> Dados obtidos diretamente do sistema RelatorioAPS do Ministerio da Saude.
              Representam os valores efetivamente repassados ao municipio pelo Fundo Nacional de Saude (FNS).
            </Typography>
          </Alert>

          {loadingPagamento ? (
            <><SkeletonCards /><SkeletonTable /></>
          ) : (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={classQualidadeSb ? true : 3}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Total Mensal Recebido</Typography>
                        <InfoTip title="Soma de todos os componentes de pagamento efetivamente repassados pelo FNS ao municipio na competencia mais recente. Inclui custeio (equidade + implantacao), desempenho (vinculo + qualidade) e incentivos." />
                      </Box>
                      <Typography variant="h5" fontWeight={700} sx={{ color: '#1565c0', mt: 0.5 }}>
                        {formatCurrency(totalMensal)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={classQualidadeSb ? true : 3}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Vinculo eSF/eAP</Typography>
                        <InfoTip title="Classificacao de desempenho das equipes eSF/eAP no componente de Vinculo e Acompanhamento Territorial do Previne Brasil." />
                      </Box>
                      <Chip label={classVinculo} sx={{ mt: 1, fontWeight: 700, fontSize: '0.875rem', bgcolor: alpha(getClassColor(classVinculo), 0.12), color: getClassColor(classVinculo) }} />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={classQualidadeSb ? true : 3}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Qualidade eSF/eAP</Typography>
                        <InfoTip title="Classificacao de desempenho das equipes eSF/eAP nos indicadores de qualidade do Previne Brasil (C1 a C7)." />
                      </Box>
                      <Chip label={classQualidadeEsf} sx={{ mt: 1, fontWeight: 700, fontSize: '0.875rem', bgcolor: alpha(getClassColor(classQualidadeEsf), 0.12), color: getClassColor(classQualidadeEsf) }} />
                    </CardContent>
                  </Card>
                </Grid>
                {classQualidadeSb && (
                  <Grid item xs={12} sm={6} md>
                    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Qualidade eSB</Typography>
                          <InfoTip title="Classificacao de desempenho das equipes de Saude Bucal (eSB) nos indicadores de qualidade B1 a B6." />
                        </Box>
                        <Chip label={classQualidadeSb} sx={{ mt: 1, fontWeight: 700, fontSize: '0.875rem', bgcolor: alpha(getClassColor(classQualidadeSb), 0.12), color: getClassColor(classQualidadeSb) }} />
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={classQualidadeSb ? true : 3}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Qualidade eMulti</Typography>
                        <InfoTip title="Classificacao de desempenho das equipes eMulti (equipes multiprofissionais) nos indicadores de qualidade especificos." />
                      </Box>
                      <Chip label={classQualidadeMulti} sx={{ mt: 1, fontWeight: 700, fontSize: '0.875rem', bgcolor: alpha(getClassColor(classQualidadeMulti), 0.12), color: getClassColor(classQualidadeMulti) }} />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Payment Components Table */}
              {componentes.length > 0 && (
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600}>Componentes de Pagamento</Typography>
                      <InfoTip title="Detalhamento dos valores repassados pelo Ministerio da Saude por componente." />
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                            <TableCell>Componente</TableCell>
                            <TableCell align="center">Equipes Pagas</TableCell>
                            <TableCell align="right">Valor Custeio</TableCell>
                            <TableCell align="right">Valor Qualidade</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {componentes.map((comp: any, idx: number) => (
                            <TableRow key={idx} hover>
                              <TableCell><Typography variant="body2" fontWeight={500}>{comp.nome || '-'}</Typography></TableCell>
                              <TableCell align="center">{comp.qtEquipes ?? '-'}</TableCell>
                              <TableCell align="right">{formatCurrency(comp.vlCusteio ?? 0)}</TableCell>
                              <TableCell align="right">{formatCurrency(comp.vlQualidade ?? 0)}</TableCell>
                              <TableCell align="right"><Typography variant="body2" fontWeight={600}>{formatCurrency(comp.vlTotal ?? 0)}</Typography></TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                            <TableCell colSpan={4}><Typography variant="body2" fontWeight={700}>Total Geral</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" fontWeight={700} sx={{ color: '#1565c0' }}>{formatCurrency(totalMensal)}</Typography></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* Comparison: NeoGestor Simulation vs Real Payment */}
              {loadingSimulacao ? (
                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 3, mb: 3 }} />
              ) : resumoComparativo?.simulacao && (
                <Card elevation={0} sx={{ borderRadius: 3, border: `2px solid ${theme.palette.primary.main}`, mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CompareArrowsIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="subtitle1" fontWeight={600} color="primary">Simulacao NeoGestor vs Pagamento Real</Typography>
                      <InfoTip title="Comparativo entre os valores calculados pelo simulador NeoGestor e os valores efetivamente repassados pelo Ministerio da Saude." />
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Card elevation={0} sx={{ borderRadius: 2, bgcolor: alpha('#1565c0', 0.04), border: `1px solid ${alpha('#1565c0', 0.2)}` }}>
                          <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="caption" color="text.secondary">Pagamento Real (MS)</Typography>
                            <Typography variant="h6" fontWeight={700} sx={{ color: '#1565c0' }}>{formatCurrency(totalMensal)}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Card elevation={0} sx={{ borderRadius: 2, bgcolor: alpha('#2e7d32', 0.04), border: `1px solid ${alpha('#2e7d32', 0.2)}` }}>
                          <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                            <Typography variant="caption" color="text.secondary">Simulacao NeoGestor</Typography>
                            <Typography variant="h6" fontWeight={700} sx={{ color: '#2e7d32' }}>{formatCurrency(resumoComparativo.simulacao.valorSimulado || 0)}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                    {resumoComparativo.simulacao.delta != null && resumoComparativo.simulacao.delta !== 0 && (
                      <Alert severity={resumoComparativo.simulacao.delta > 0 ? 'success' : 'warning'} sx={{ mt: 2, borderRadius: 2 }}>
                        Diferenca de <strong>{formatCurrency(Math.abs(resumoComparativo.simulacao.delta))}/mes</strong> entre o valor simulado e o pagamento real.
                        {resumoComparativo.simulacao.delta > 0
                          ? ' O municipio tem potencial para receber mais com melhoria dos indicadores.'
                          : ' O valor real esta abaixo da simulacao, possivelmente por equipes suspensas ou dados desatualizados.'}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Equipes Homologadas */}
              {loadingEquipes ? (
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
              ) : Array.isArray(equipesHomologadasList) && equipesHomologadasList.length > 0 && (
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <GroupsIcon sx={{ mr: 1, color: '#1565c0' }} />
                      <Typography variant="subtitle1" fontWeight={600}>Equipes Homologadas</Typography>
                      <InfoTip title="Equipes oficialmente registradas e homologadas pelo Ministerio da Saude para recebimento de financiamento federal." />
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                            <TableCell>INE</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Subtipo</TableCell>
                            <TableCell>Portaria</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {equipesHomologadasList.map((eq: any, idx: number) => (
                            <TableRow key={idx} hover>
                              <TableCell><Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>{eq.ine || eq.codigoEquipe || '-'}</Typography></TableCell>
                              <TableCell>{eq.tipoDescricao || eq.tipo || eq.descricaoTipoEquipe || '-'}</TableCell>
                              <TableCell>{eq.subtipo || eq.descricaoSubtipoEquipe || '-'}</TableCell>
                              <TableCell>{eq.portaria || eq.descricaoPortariaHomologacao || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabPanel>

        {/* ═══════════════════════════════════════════════ */}
        {/* TAB 2: ALERTAS DE SUSPENSAO */}
        {/* ═══════════════════════════════════════════════ */}
        <TabPanel value={tabValue} index={1}>
          <Alert severity="warning" sx={{ borderRadius: 2, mb: 3 }} icon={<ReportProblemIcon />}>
            <Typography variant="body2">
              <strong>Equipes e Servicos em Risco de Suspensao.</strong> Conforme normativa vigente, a ausencia do envio de informacoes de producao
              valida ao SIAPS por tres competencias consecutivas acarreta a suspensao total do valor do financiamento da respectiva equipe ou servico.
            </Typography>
          </Alert>

          {loadingSuspensao ? (
            <><SkeletonCards /><SkeletonTable /></>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Equipes em Risco</Typography>
                        <InfoTip title="Numero total de equipes que nao enviaram producao valida ao SIAPS em pelo menos uma competencia consecutiva." />
                      </Box>
                      <Typography variant="h4" fontWeight={700} sx={{ color: totalEquipesRisco > 0 ? '#f44336' : '#4caf50', mt: 0.5 }}>{totalEquipesRisco}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Estabelecimentos</Typography>
                        <InfoTip title="Estabelecimentos de saude com pendencias de producao ou cadastro junto ao SCNES." />
                      </Box>
                      <Typography variant="h4" fontWeight={700} sx={{ color: totalEstabelecimentosRisco > 0 ? '#ff9800' : '#4caf50', mt: 0.5 }}>{totalEstabelecimentosRisco}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Profissionais</Typography>
                        <InfoTip title="Profissionais individuais (ACS, TSB) com competencias sem envio de producao." />
                      </Box>
                      <Typography variant="h4" fontWeight={700} sx={{ color: totalProfissionaisRisco > 0 ? '#ff9800' : '#4caf50', mt: 0.5 }}>{totalProfissionaisRisco}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {equipesCriticas.length > 0 && (
                <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }} icon={<ReportProblemIcon />}>
                  <Typography variant="body2">
                    <strong>CRITICO:</strong> {equipesCriticas.length} equipe(s) com 3 ou mais competencias consecutivas sem envio de producao. O financiamento destas equipes ja pode estar suspenso ou sera suspenso na proxima competencia.
                  </Typography>
                </Alert>
              )}

              {equipesRisco.length > 0 && (
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600}>Equipes em Risco</Typography>
                      <InfoTip title="Lista de equipes com competencias consecutivas sem envio de producao valida ao SIAPS." />
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha('#f44336', 0.04) }}>
                            <TableCell>INE</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Motivo</TableCell>
                            <TableCell align="center">Competencias Consecutivas</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {equipesRisco.map((eq: any, idx: number) => {
                            const compNum = parseCompetencias(eq.qtCompetenciasConsecutivas);
                            return (
                              <TableRow key={idx} hover>
                                <TableCell><Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>{eq.coEquipe || eq.nuIne || '-'}</Typography></TableCell>
                                <TableCell>{eq.coTipoEquipe || eq.tipo || '-'}</TableCell>
                                <TableCell><Typography variant="body2" sx={{ maxWidth: 300 }}>{eq.dsSubTipoSuspensao || 'Ausencia de producao valida'}</Typography></TableCell>
                                <TableCell align="center">
                                  <Chip label={`${compNum} ${compNum === 1 ? 'mes' : 'meses'}`} size="small" sx={{ fontWeight: 700, bgcolor: alpha(getCompetenciasColor(compNum), 0.15), color: getCompetenciasColor(compNum), border: `1px solid ${alpha(getCompetenciasColor(compNum), 0.3)}` }} />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {estabelecimentosRisco.length > 0 && (
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600}>Estabelecimentos em Risco</Typography>
                      <InfoTip title="Estabelecimentos de saude com pendencias que podem afetar o financiamento das equipes vinculadas." />
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha('#ff9800', 0.04) }}>
                            <TableCell>CNES</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Gestao</TableCell>
                            <TableCell>Motivo</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {estabelecimentosRisco.map((est: any, idx: number) => (
                            <TableRow key={idx} hover>
                              <TableCell><Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>{est.coCnes || est.nuCnes || '-'}</Typography></TableCell>
                              <TableCell>{est.dsComponentePagamento || est.dsTipo || '-'}</TableCell>
                              <TableCell>{est.dsEsferaAdministrativa || est.dsGestao || '-'}</TableCell>
                              <TableCell><Typography variant="body2" sx={{ maxWidth: 300 }}>{est.dsSubTipoSuspensao || '-'}</Typography></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {profissionaisRisco.length > 0 && (
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600}>Profissionais (ACS/TSB) em Risco</Typography>
                      <InfoTip title="Agentes Comunitarios de Saude (ACS) e Tecnicos em Saude Bucal (TSB) cujo financiamento individual esta em risco." />
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha('#ff9800', 0.04) }}>
                            <TableCell>INE Equipe</TableCell>
                            <TableCell>CBO</TableCell>
                            <TableCell>CNS</TableCell>
                            <TableCell align="center">Competencias</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {profissionaisRisco.map((prof: any, idx: number) => {
                            const compNum = parseCompetencias(prof.qtCompetenciasConsecutivas);
                            return (
                              <TableRow key={idx} hover>
                                <TableCell><Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>{prof.coEquipe || prof.nuIne || '-'}</Typography></TableCell>
                                <TableCell>{prof.coCbo || prof.cbo || '-'}</TableCell>
                                <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{prof.nuCns || '-'}</Typography></TableCell>
                                <TableCell align="center">
                                  <Chip label={`${compNum}`} size="small" sx={{ fontWeight: 700, bgcolor: alpha(getCompetenciasColor(compNum), 0.15), color: getCompetenciasColor(compNum) }} />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {totalEquipesRisco === 0 && totalEstabelecimentosRisco === 0 && totalProfissionaisRisco === 0 && (
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                  <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
                    <Typography variant="h6" fontWeight={600} gutterBottom>Nenhum alerta de suspensao</Typography>
                    <Typography variant="body2" color="text.secondary">Todas as equipes e servicos do municipio estao com envio de producao em dia.</Typography>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabPanel>

        {/* ═══════════════════════════════════════════════ */}
        {/* TAB 3: COBERTURA APS */}
        {/* ═══════════════════════════════════════════════ */}
        <TabPanel value={tabValue} index={2}>
          <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }} icon={<LocalHospitalIcon />}>
            <Typography variant="body2">
              <strong>Cobertura da Atencao Primaria a Saude.</strong> Percentual da populacao coberta por equipes de saude da familia e atencao primaria,
              calculado pelo Ministerio da Saude com base nos dados do SCNES (Sistema de Cadastro Nacional de Estabelecimentos de Saude).
            </Typography>
          </Alert>

          {(loadingCoberturaAps && loadingCoberturaSb) ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3, mb: 3 }} />
                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 3, mb: 2 }} />
                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 3 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 3, mb: 3 }} />
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3, mb: 3 }} />
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600}>Cobertura Atual</Typography>
                      <InfoTip title="Percentuais de cobertura calculados pelo MS com base no numero de equipes cadastradas no SCNES e a populacao estimada pelo IBGE." />
                    </Box>
                    <CoverageBar label="Cobertura APS" value={cobApsPercent} color="#1565c0" tooltip="Percentual da populacao municipal coberta por equipes de Atencao Primaria a Saude (eSF + eAP). Meta do Plano Nacional: 80%." />
                    <CoverageBar label="Cobertura Saude Bucal" value={cobSbPercent} color="#7b1fa2" tooltip="Percentual da populacao coberta por equipes de Saude Bucal (eSB)." />
                    {cobAcsPercent != null && (
                      <CoverageBar label="Cobertura ACS" value={cobAcsPercent} color="#00796b" tooltip="Percentual da populacao coberta por Agentes Comunitarios de Saude." />
                    )}
                  </CardContent>
                </Card>
                <BenchmarkRow municipio={cobApsPercent} estado={cobEstado} brasil={cobBrasil} label="Cobertura APS - Comparativo" tooltip="Comparacao da cobertura de Atencao Primaria do municipio com a media estadual e nacional." />
                <BenchmarkRow municipio={cobSbPercent} estado={cobSbEstado} brasil={cobSbBrasil} label="Cobertura Saude Bucal - Comparativo" tooltip="Comparacao da cobertura de Saude Bucal do municipio com as medias estadual e nacional." />
              </Grid>
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <GroupsIcon sx={{ mr: 1, color: '#1565c0' }} />
                      <Typography variant="subtitle1" fontWeight={600}>Composicao de Equipes</Typography>
                      <InfoTip title="Quantidade de equipes de Atencao Primaria credenciadas e ativas no municipio, por tipo e carga horaria." />
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: alpha('#1565c0', 0.06) }}>
                          <Typography variant="caption" color="text.secondary">eSF</Typography>
                          <Typography variant="h5" fontWeight={700} sx={{ color: '#1565c0' }}>{esfCount}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: alpha('#2e7d32', 0.06) }}>
                          <Typography variant="caption" color="text.secondary">eAP 30h</Typography>
                          <Typography variant="h5" fontWeight={700} sx={{ color: '#2e7d32' }}>{eap30Count}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: alpha('#e65100', 0.06) }}>
                          <Typography variant="caption" color="text.secondary">eAP 20h</Typography>
                          <Typography variant="h5" fontWeight={700} sx={{ color: '#e65100' }}>{eap20Count}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Populacao Estimada</Typography>
                          <InfoTip title="Populacao estimada pelo IBGE utilizada como base para o calculo dos percentuais de cobertura." />
                        </Box>
                        <Typography variant="h5" fontWeight={700}>{populacao ? populacao.toLocaleString('pt-BR') : '-'}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Typography variant="caption" color="text.secondary">Ano de Referencia</Typography>
                          <InfoTip title="Ano da estimativa populacional do IBGE utilizada pelo Ministerio da Saude para os calculos de cobertura." />
                        </Box>
                        <Typography variant="h5" fontWeight={700} color="text.secondary">{anoReferencia}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
                {coberturaTimeSeries.length > 1 && (
                  <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TrendingUpIcon sx={{ mr: 1, color: '#1565c0' }} />
                        <Typography variant="subtitle1" fontWeight={600}>Evolucao da Cobertura</Typography>
                        <InfoTip title="Evolucao historica dos percentuais de cobertura APS e Saude Bucal nos ultimos 12 meses." />
                      </Box>
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={coberturaTimeSeries} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.08)} />
                          <XAxis dataKey="competencia" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} domain={[0, 'auto']} tickFormatter={(v: number) => `${v}%`} />
                          <RechartsTooltip formatter={(value: any) => [`${Number(value).toFixed(1)}%`]} contentStyle={{ borderRadius: 8, border: '1px solid #e0e0e0' }} />
                          <Legend />
                          <Line type="monotone" dataKey="aps" name="Cobertura APS" stroke="#1565c0" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                          {coberturaTimeSeries.some((d: any) => d.sb > 0) && (
                            <Line type="monotone" dataKey="sb" name="Saude Bucal" stroke="#7b1fa2" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </Grid>
            </Grid>
          )}
        </TabPanel>
      </Box>
    </Fade>
    {municipioAtivo && dadosContexto && (
      <AnalisePredicaoCard
        dadosContexto={dadosContexto}
        municipioId={municipioAtivo.id}
        painelId="painel-ms"
        manualTrigger
      />
    )}
    <ChatIAButton contexto="saude" dadosContexto={dadosContexto} fabSx={{ bottom: 24, right: 24, top: 'auto' }} />
    </>
  );
};

export default SaudePainelMS;
